import { SearchInput } from '@heidi/design-system/search-input';
import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

interface SearchDropdownLayoutProps {
  containerRef: React.Ref<HTMLDivElement>;
  inputRef: React.Ref<HTMLInputElement>;
  query: string;
  onQueryChange: (value: string) => void;
  onOpen: () => void;
  placeholder: string;
  autoFocus?: boolean;
  className?: string;
  inputTestId?: string;
  /**
   * Extra classes for the dropdown panel itself. Merged via ``cn`` so
   * consumers can escalate the border / shadow when the popup floats over
   * content (e.g. the curated source list in the create-collection wizard)
   * and needs to read as a clearly separate layer.
   */
  panelClassName?: string;
  /** Rendered inside the dropdown panel (only when `showDropdown` is true) */
  children?: ReactNode;
  showDropdown: boolean;
}

/**
 * Renders the search `<SearchInput>` + absolute-positioned dropdown container
 * shared by `DocumentSearchInput` and `SourceSearchInput`.
 */
export const SearchDropdownLayout = ({
  containerRef,
  inputRef,
  query,
  onQueryChange,
  onOpen,
  placeholder,
  autoFocus = false,
  className,
  inputTestId,
  panelClassName,
  children,
  showDropdown,
}: SearchDropdownLayoutProps) => {
  const intl = useIntl();
  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <SearchInput
        ref={inputRef}
        data-testid={inputTestId}
        placeholder={placeholder}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
        value={query}
        onChange={(e) => {
          onQueryChange(e.target.value);
          onOpen();
        }}
        onFocus={onOpen}
        onClick={onOpen}
        autoFocus={autoFocus}
      />
      {showDropdown && (
        <div
          className={cn(
            'absolute inset-x-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-border bg-surface shadow-lg',
            panelClassName
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};
